import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './VideoDialog.module.scss';
import { useTranslation } from 'react-i18next';
import UploadIcon from '../icons/UploadIcon';
import type { BaseVideoDialogProps } from '../../types';

export interface VideoDialogProps extends BaseVideoDialogProps {
    // Конфигурация полей для отображения (все значения по умолчанию - false)
    showTitle?: boolean;
    showDescription?: boolean;
    titleRequired?: boolean;
    descriptionRequired?: boolean;
    defaultPublishTime?: string; // Время публикации по умолчанию
    showPublishTime?: boolean; // Показывать ли инпут времени публикации
}

const VideoDialog: React.FC<VideoDialogProps> = ({
    open,
    onClose,
    onSubmit,
    dialogTitle,
    initialData,
    showTitle = false,
    showDescription = false,
    titleRequired = false,
    descriptionRequired = false,
    defaultPublishTime = '10:00', // Время публикации по умолчанию
    showPublishTime = false // Показывать ли инпут времени публикации
}) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState(initialData?.title || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [videoType, setVideoType] = useState<'youtube' | 'file'>(initialData?.videoType || 'file');
    const [youtubeUrl, setYoutubeUrl] = useState(initialData?.youtubeUrl || '');
    const [videoFile, setVideoFile] = useState<File | undefined>(undefined);
    const [thumbnailFile, setThumbnailFile] = useState<File | undefined>(undefined);
    const [generatePreviewFromVideo, setGeneratePreviewFromVideo] = useState(false);
    const [previewTime, setPreviewTime] = useState('');
    const [publishTime, setPublishTime] = useState(initialData?.publishTime || defaultPublishTime);
    const [publishDate, setPublishDate] = useState(initialData?.publishDate || new Date().toISOString().split('T')[0]);
    const [error, setError] = useState('');
    const [language] = useState<'ru' | 'it'>(initialData?.language || 'ru');
    const videoInputRef = useRef<HTMLInputElement>(null);
    const thumbnailInputRef = useRef<HTMLInputElement>(null);
    // Отслеживание измененных полей
    const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
    // Состояние отправки формы
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleReset = useCallback(() => {
        setTitle('');
        setDescription('');
        setVideoType('file');
        setYoutubeUrl('');
        setVideoFile(undefined);
        setThumbnailFile(undefined);
        setGeneratePreviewFromVideo(false);
        setPreviewTime('');
        setPublishTime(defaultPublishTime);
        setPublishDate(new Date().toISOString().split('T')[0]);
        setError('');
        setModifiedFields(new Set());
        onClose();
    }, [onClose, defaultPublishTime]);

    const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            handleReset();
        }
    }, [isSubmitting, handleReset]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [open]);

    useEffect(() => {
        if (initialData) {
            setTitle(initialData.title || '');
            setDescription(initialData.description || '');
            setVideoType(initialData.videoType || 'youtube');
            setYoutubeUrl(initialData.youtubeUrl || '');
            setGeneratePreviewFromVideo(false);
            setPreviewTime('');
            setVideoFile(undefined);
            setThumbnailFile(undefined);
            setPublishTime(initialData.publishTime || defaultPublishTime);
            setPublishDate(initialData.publishDate || new Date().toISOString().split('T')[0]);
            // Сбрасываем отслеживание изменений при обновлении initialData
            setModifiedFields(new Set());
        } else {
            // Сброс к значениям по умолчанию
            setVideoType('file');
            setPublishTime(defaultPublishTime);
            setPublishDate(new Date().toISOString().split('T')[0]);
        }
    }, [initialData, defaultPublishTime]);

    // Отслеживание изменений полей
    const trackChange = (field: string) => {
        setModifiedFields(prev => {
            const updated = new Set(prev);
            updated.add(field);
            return updated;
        });
    };

    // Обработчики изменения полей с отслеживанием
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
        trackChange('title');
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value);
        trackChange('description');
    };

    const handleVideoTypeChange = (type: 'youtube' | 'file') => {
        setVideoType(type);
        trackChange('videoType');
    };

    const handleYoutubeUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setYoutubeUrl(e.target.value);
        trackChange('youtubeUrl');
    };

    const handleGeneratePreviewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGeneratePreviewFromVideo(e.target.checked);
        trackChange('generatePreviewFromVideo');
    };

    const handlePreviewTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPreviewTime(e.target.value);
        trackChange('previewTime');
    };

    const handlePublishTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPublishTime(e.target.value);
        trackChange('publishTime');
    };

    const handlePublishDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPublishDate(e.target.value);
        trackChange('publishDate');
    };

    const handleVideoButtonClick = () => {
        if (videoInputRef.current) {
            videoInputRef.current.click();
        }
    };

    const handleThumbnailButtonClick = () => {
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.click();
        }
    };

    const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || undefined;
        setVideoFile(file);
        if (file) {
            trackChange('videoFile');
        }
    };

    const handleThumbnailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || undefined;
        setThumbnailFile(file);
        if (file) trackChange('thumbnailFile');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');


        // Валидация полей
        if (showTitle && titleRequired && !title.trim()) {
            setError(t('common.titleRequired'));
            return;
        }

        if (showDescription && descriptionRequired && !description.trim()) {
            setError(t('common.descriptionRequired'));
            return;
        }

        // Проверка YouTube URL только при создании нового видео или при изменении типа на YouTube
        if (videoType === 'youtube' && !youtubeUrl.trim() && (!initialData?.id || modifiedFields.has('videoType'))) {
            setError(t('common.youtubeUrlRequired'));
            return;
        }

        // Проверка видеофайла только при создании нового видео или при изменении типа на File
        if (videoType === 'file' && !videoFile && !initialData?.existingVideoUrl && (!initialData?.id || modifiedFields.has('videoType'))) {
            setError(t('common.videoFileRequired'));
            return;
        }

        // Проверка превью только при создании нового видео, выборе файла и отключенной генерации превью
        if (!generatePreviewFromVideo && !thumbnailFile && !initialData?.existingThumbnailUrl && videoType === 'file' && !initialData?.id) {
            setError(t('video.thumbnailRequired'));
            return;
        }

        try {
            // Устанавливаем флаг отправки
            setIsSubmitting(true);

            // Используем существующие данные при редактировании или новый объект при создании
            const baseTrainingData = initialData?.training || {};

            // Строим данные только с измененными полями
            const trainingData: any = { ...baseTrainingData };

            // Добавляем поля только если они были изменены или необходимы для отправки
            if (showTitle && modifiedFields.has('title')) trainingData.title = title;
            if (showDescription && modifiedFields.has('description')) trainingData.description = description;

            if (modifiedFields.has('videoType') || !initialData) {
                // Всегда включаем поля, связанные с типом видео, если они изменились или это новая запись
                if (videoType === 'youtube') {
                    trainingData.youtube_url = youtubeUrl;
                    // Удаляем поля, связанные с файлом, при переключении на YouTube
                    delete trainingData.video_file_path;
                } else {
                    // Удаляем URL YouTube при переключении на файл
                    delete trainingData.youtube_url;
                }
            } else if (videoType === 'youtube' && modifiedFields.has('youtubeUrl')) {
                trainingData.youtube_url = youtubeUrl;
            }

            // Обработка настроек превью
            if (modifiedFields.has('generatePreviewFromVideo') || !initialData) {
                trainingData.generate_preview_from_video = generatePreviewFromVideo;

                if (generatePreviewFromVideo) {
                    if (previewTime) trainingData.preview_time = previewTime;
                    // Удаляем файл превью, если генерируем из видео
                    delete trainingData.preview_file_path;
                }
            }

            // Всегда включаем язык
            trainingData.language = language;

            // Добавляем время и дату публикации для всех записей
            trainingData.time = publishTime;
            trainingData.date = publishDate;

            await onSubmit({
                training: trainingData,
                videoFile,
                thumbnailFile
            });

            handleReset();
        } catch (err) {
            setError(t('common.uploadError'));
        } finally {
            // Сбрасываем флаг отправки
            setIsSubmitting(false);
        }
    };

    // Обработчик закрытия для кнопки закрытия
    const handleCloseClick = () => {
        if (!isSubmitting) {
            handleReset();
        }
    };

    if (!open) return null;

    return (
        <div className={styles.dialogOverlay} onClick={handleBackdropClick}>
            <div className={styles.dialogContainer}>
                <div className={styles.dialogHeader}>
                    <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleCloseClick}
                        aria-label={t('common.close')}
                        disabled={isSubmitting}
                    >
                        ✕
                    </button>
                </div>
                <div className={styles.dialogContent}>
                    <form onSubmit={handleSubmit} className={styles.formContainer}>
                        {showTitle && (
                            <div className={styles.formField}>
                                <label htmlFor="title" className={styles.label}>
                                    {t('common.title')}
                                </label>
                                <input
                                    id="title"
                                    className={styles.input}
                                    type="text"
                                    value={title}
                                    onChange={handleTitleChange}
                                    required={titleRequired}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}

                        {showDescription && (
                            <div className={styles.formField}>
                                <label htmlFor="description" className={styles.label}>
                                    {t('common.description')}
                                </label>
                                <textarea
                                    id="description"
                                    className={styles.textarea}
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    rows={4}
                                    required={descriptionRequired}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}

                        {/* Добавляем инпуты даты и времени публикации */}
                        {showPublishTime && (
                            <div className={styles.publishDateTimeRow}>
                                <div className={`${styles.formField} ${styles.publishDateTimeField}`}>
                                    <label htmlFor="publishDate" className={styles.label}>
                                        {t('dailyTrainings.publishDate')}
                                    </label>
                                    <input
                                        id="publishDate"
                                        className={styles.input}
                                        type="date"
                                        value={publishDate}
                                        onChange={handlePublishDateChange}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className={`${styles.formField} ${styles.publishDateTimeField}`}>
                                    <label htmlFor="publishTime" className={styles.label}>
                                        {t('dailyTrainings.publishTime')}
                                    </label>
                                    <input
                                        id="publishTime"
                                        className={styles.input}
                                        type="time"
                                        value={publishTime}
                                        onChange={handlePublishTimeChange}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.radioGroup}>
                            <label className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="videoType"
                                    value="youtube"
                                    checked={videoType === 'youtube'}
                                    onChange={() => handleVideoTypeChange('youtube')}
                                    disabled={isSubmitting}
                                />
                                {t('video.youtube')}
                            </label>
                            <label className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="videoType"
                                    value="file"
                                    checked={videoType === 'file'}
                                    onChange={() => handleVideoTypeChange('file')}
                                    disabled={isSubmitting}
                                />
                                {t('video.file')}
                            </label>
                        </div>

                        {videoType === 'youtube' ? (
                            <div className={styles.formField}>
                                <label htmlFor="youtubeUrl" className={styles.label}>
                                    {t('video.youtubeUrl')}
                                </label>
                                <input
                                    id="youtubeUrl"
                                    className={styles.input}
                                    type="text"
                                    value={youtubeUrl}
                                    onChange={handleYoutubeUrlChange}
                                    required={videoType === 'youtube'}
                                    disabled={isSubmitting}
                                />
                            </div>
                        ) : (
                            <div>
                                <input
                                    className={styles.fileInput}
                                    accept="video/*"
                                    id="video-file"
                                    type="file"
                                    ref={videoInputRef}
                                    onChange={handleVideoFileChange}
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="video-file" className={styles.fileInputLabel}>
                                    <button
                                        type="button"
                                        className={`${styles.button} ${styles.buttonOutlined}`}
                                        onClick={handleVideoButtonClick}
                                        disabled={isSubmitting}
                                    >
                                        <UploadIcon
                                            width={18}
                                            height={18}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {videoFile ? videoFile.name : initialData?.existingVideoUrl ? t('video.normal') : t('video.uploadVideo')}
                                    </button>
                                </label>
                            </div>
                        )}

                        <div>
                            <p className={styles.subtitle}>{t('video.preview')}</p>

                            <div className={styles.checkboxField}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={generatePreviewFromVideo}
                                        onChange={handleGeneratePreviewChange}
                                        className={styles.checkbox}
                                        disabled={isSubmitting}
                                    />
                                    {t('video.generatePreview')}
                                </label>

                                {generatePreviewFromVideo && (
                                    <div className={styles.formField}>
                                        <input
                                            className={styles.input}
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={previewTime}
                                            onChange={handlePreviewTimeChange}
                                            placeholder={t('video.previewTimeHelp')}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {!generatePreviewFromVideo && (
                            <div>
                                <input
                                    className={styles.fileInput}
                                    accept="image/*"
                                    id="thumbnail-file"
                                    type="file"
                                    ref={thumbnailInputRef}
                                    onChange={handleThumbnailFileChange}
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="thumbnail-file" className={styles.fileInputLabel}>
                                    <button
                                        type="button"
                                        className={`${styles.button} ${styles.buttonOutlined}`}
                                        onClick={handleThumbnailButtonClick}
                                        disabled={isSubmitting}
                                    >
                                        <UploadIcon
                                            width={18}
                                            height={18}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {thumbnailFile ? thumbnailFile.name : initialData?.existingThumbnailUrl ? t('video.preview') : t('video.uploadPreview')}
                                    </button>
                                </label>
                            </div>
                        )}

                        {error && <p className={styles.errorText}>{error}</p>}
                    </form>
                </div>
                <div className={styles.dialogActions}>
                    <button
                        onClick={handleCloseClick}
                        className={styles.button}
                        disabled={isSubmitting}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleSubmit}
                        className={`${styles.button} ${styles.buttonContained}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className={styles.spinnerContainer}>
                                <span className={styles.spinner}></span>
                                <span style={{ marginLeft: '8px' }}>{t('common.saving')}</span>
                            </span>
                        ) : t('common.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Экспорт предустановленных конфигураций для удобства использования
export const BonusVideosDialog: React.FC<BaseVideoDialogProps> = (props) => (
    <VideoDialog {...props} showTitle showDescription titleRequired descriptionRequired />
);

export const FreeVideoDialog: React.FC<BaseVideoDialogProps> = (props) => (
    <VideoDialog {...props} />
);

export const InstructionDialog: React.FC<BaseVideoDialogProps> = (props) => (
    <VideoDialog {...props} showDescription descriptionRequired />
);

export const PresentationDialog: React.FC<BaseVideoDialogProps> = (props) => (
    <VideoDialog {...props} />
);

export interface VideoUploadDialogProps extends BaseVideoDialogProps {
    hideTextFields?: boolean;
    defaultPublishTime?: string;
    showPublishTime?: boolean; // Добавляем новый проп
}

export const VideoUploadDialog: React.FC<VideoUploadDialogProps> = ({
    hideTextFields = false,
    defaultPublishTime = '10:00',
    showPublishTime = false, // Добавляем новый проп
    ...props
}) => (
    <VideoDialog
        {...props}
        showTitle={!hideTextFields}
        showDescription={!hideTextFields}
        titleRequired={!hideTextFields}
        descriptionRequired={!hideTextFields}
        defaultPublishTime={defaultPublishTime}
        showPublishTime={showPublishTime} // Передаем новый проп
    />
);

export default VideoDialog; 